import { createPortal } from 'react-dom'
import { useState, useEffect } from 'react'
import { enqueueSnackbar as enqueueSnackbarAction } from '../../../store/actions'

import { Box, Typography, Dialog, DialogActions, DialogContent, DialogTitle, Stack, OutlinedInput } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { StyledButton } from '../button/StyledButton'

// Icons
import { IconCopy, IconPlus } from '@tabler/icons'

// Analytics
import { track, eventTypes } from 'torqcloud-ui'

// API
import ApiKeyApi, { ApiKey } from '../../../api/agentBuilder/apikey'

// utils
import useNotifier from '../../../utils/useNotifier'
import useApiClient from '../../../hooks/useApiClient'
import { asDispatchFn, useDispatch } from '../../../store'

interface DialogPropsType {
  title: string
  type: 'ADD' | 'EDIT'
  cancelButtonName?: string
  confirmButtonName: string
  key?: ApiKey
}

interface APIKeyDialogProps {
  show: boolean
  dialogProps: DialogPropsType
  onCancel: () => void
  onConfirm: () => void
}

const APIKeyDialog = ({ show, dialogProps, onCancel, onConfirm }: APIKeyDialogProps) => {
  const portalElement = document.getElementById('portal')

  const theme = useTheme()
  const dispatch = useDispatch()

  // ==============================|| Snackbar ||============================== //

  useNotifier()

  const enqueueSnackbar = asDispatchFn(dispatch, enqueueSnackbarAction)

  const [keyName, setKeyName] = useState<string>('')
  const [isApiKeyCreated, setIsApiKeyCreated] = useState<boolean>(false)
  const [apiKeyValue, setApiKeyValue] = useState<string>()

  function getButtonLabel() {
    if (dialogProps.type === 'EDIT') {
      return dialogProps.confirmButtonName
    } else if (apiKeyValue) {
      return (
        <>
          <IconCopy />
          Copy API Key
        </>
      )
    }
    return (
      <>
        <IconPlus />
        Create API Key
      </>
    )
  }

  useEffect(() => {
    if (dialogProps.type === 'EDIT' && dialogProps.key) {
      setKeyName(dialogProps.key.keyName)
    } else if (dialogProps.type === 'ADD') {
      setKeyName('')
    }
  }, [dialogProps])

  const apiClientContext = useApiClient()
  const apiKeyApi = new ApiKeyApi(apiClientContext.torqAgentClient)

  const addNewKey = async () => {
    if (apiKeyValue) {
      void navigator.clipboard.writeText(apiKeyValue)
      track(eventTypes.API_KEY_COPIED)
      enqueueSnackbar({
        message: 'API Key Copied!',
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'success',
        },
      })
      return
    }

    try {
      const createResp = await apiKeyApi.createNewAPI({ keyName })
      if (createResp.data) {
        enqueueSnackbar({
          message: 'New API key added',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
            dismissible: true,
          },
        })
        setIsApiKeyCreated(true)
        setApiKeyValue(createResp.data.apiKey)
        track(eventTypes.API_KEY_CREATED)
        // onConfirm()
      }
    } catch (error: any) {
      const errorData = error.response?.data || `${error.response?.status}: ${error.response?.statusText}`
      enqueueSnackbar({
        message: `Failed to add new API key: ${errorData}`,
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'error',
          persist: true,
          dismissible: true,
        },
      })
      onCancel()
    }
  }

  const saveKey = async () => {
    try {
      if (!dialogProps.key) return

      const saveResp = await apiKeyApi.updateAPI(dialogProps.key.id, {
        keyName,
      })
      if (saveResp.data) {
        enqueueSnackbar({
          message: 'API Key saved',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
            dismissible: true,
          },
        })
        track(eventTypes.API_KEY_UPDATED)
        onConfirm()
      }
    } catch (error: any) {
      const errorData = error.response?.data || `${error.response?.status}: ${error.response?.statusText}`
      enqueueSnackbar({
        message: `Failed to save API key: ${errorData}`,
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'error',
          persist: true,
          dismissible: true,
        },
      })
      onCancel()
    }
  }

  const component = show ? (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={show}
      onClose={onConfirm}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle sx={{ fontSize: '1rem' }} id="alert-dialog-title">
        {dialogProps.title}
      </DialogTitle>
      <DialogContent>
        {dialogProps.type === 'EDIT' && dialogProps.key && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="overline">API Key Prefix</Typography>
            <Typography
              sx={{
                p: 1,
                borderRadius: 10,
                backgroundColor: theme.palette.primary.light,
                fontFamily: 'monospace',
              }}
            >
              {dialogProps.key.apiKeyPrefix + '....'}
            </Typography>
          </Box>
        )}

        {!isApiKeyCreated && (
          <Box>
            <Stack sx={{ position: 'relative' }} direction="row">
              <Typography variant="overline">Key Name</Typography>
            </Stack>
            <OutlinedInput
              id="keyName"
              type="string"
              fullWidth
              placeholder="Key Name"
              value={keyName}
              name="keyName"
              onChange={e => setKeyName(e.target.value)}
            />
          </Box>
        )}
        {isApiKeyCreated && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ textWrap: 'nowrap', color: theme.palette.text.secondary }}>API Key Created:</Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>{apiKeyValue?.slice(0, 4) + '...'}</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                color: theme.palette.text.secondary,
                mt: 3,
              }}
            >
              <Typography>⚠️ Copy the API Key now, this is the last time you'll be able to access it!</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton variant="contained" onClick={() => (dialogProps.type === 'ADD' ? addNewKey() : saveKey())}>
          {getButtonLabel()}
        </StyledButton>
      </DialogActions>
    </Dialog>
  ) : null

  return portalElement ? createPortal(component, portalElement) : null
}

export default APIKeyDialog
